import { createSlice } from '@reduxjs/toolkit'
import { IParticipant, CodeRequest, TokenRequest, ISibling, IChild } from './interfaces'
import { AppThunk } from '..'
import { ParticipantCodeFormType } from '../../components/participant/ParticipantCodeForm'
import axios from 'axios'
import { addToast, setLoadedViaCode } from '../misc/misc'
import { addTokenRegistration } from '../registrations/registrations'
import { setAnsweredParticipantCustomQuestions } from '../program/program'
import { ToastType } from '../misc/interfaces'
import qs from 'qs'
import Moment from 'moment'
import * as routes from '../../routes'
import { IProgram } from '../program/interfaces'
import { CustomAnswer, IEvent } from '../events/interfaces'
import {
  setPaymentMethod,
} from '../paymentData'
import {Dispatch, SetStateAction} from "react";
import { NavigateFunction } from 'react-router-dom'

const participantSlice = createSlice({
  name: 'participant',
  initialState: {
    data: {} as IParticipant,
    loaded: false,
  },
  reducers: {
    setParticipant(state, action) {
      state.data = action.payload
      state.data.originalRegistrationIds = []
    },
    setParticipantBirthday(state, action) {
      state.data.date_of_birth = action.payload
    },
    setParticipantAccessKey(state, action) {
      state.data.access_key = action.payload
    },
    addTokenOriginalRegistrationID(state, action) {
      state.data.originalRegistrationIds.push(action.payload)
    },
    setParticipantSibling(state, action) {
      state.data.siblings = action.payload
    },
    removeParticipantSibling(state, action) {
      state.data.siblings = state.data.siblings.filter((sibling: ISibling) => {
        return sibling.id !== action.payload.id
      })
    },
    setParticipantChildren(state, action) {
      state.data.children = action.payload
    },
    removeParticipantChild(state, action) {
      state.data.children = state.data.children.filter((child: IChild) => {
        return child.id !== action.payload.id
      })
    },
    setDisability(state, action) {
      state.data.disabled = action.payload
    },
  },
})

export default participantSlice.reducer
export const {
  setParticipant,
  setParticipantBirthday,
  setParticipantAccessKey,
  addTokenOriginalRegistrationID,
  setParticipantSibling,
  removeParticipantSibling,
  setParticipantChildren,
  removeParticipantChild,
  setDisability,
} = participantSlice.actions

export const setParticipantViaCode = (
  data: ParticipantCodeFormType,
  program: IProgram,
  setShow: Dispatch<SetStateAction<boolean>>,
  navigate: NavigateFunction
): AppThunk => async (dispatch, getState) => {
  const { baseUrl, programId } = getState().misc
  const birthday: string = Moment(data.birthday, 'DD.MM.YYYY').format(
    'YYYY-MM-DD'
  )
  const requestData: CodeRequest = {
    program: programId,
    access_key: data.code,
    date_of_birth: birthday,
    email: data.email,
  }
  try {
    const response = await axios({
      method: 'POST',
      url: `${baseUrl}/api/custom/verify/account/`,
      data: qs.stringify(requestData),
    })
    const response_data = response.data[0]
    response_data.date_of_birth = Moment(
      response_data.date_of_birth,
      'YYYY-MM-DD'
    ).format('DD.MM.YYYY')
    dispatch(setParticipant(response_data))
    dispatch(setParticipantAccessKey(data.code))
    dispatch(setLoadedViaCode(true))

    const answeredQuestions: any[] = []

    program.participant_questions.forEach(answer => {
      response_data.customer_answers.forEach((givenAnswer: CustomAnswer) => {
        if (givenAnswer.question_id === answer.id) {
          answeredQuestions.push(answer.id)
        }
      })
    });

    dispatch(setAnsweredParticipantCustomQuestions(answeredQuestions))
    setShow(false)
  } catch (error: unknown) {
    console.error(error)
    dispatch(
      addToast({
        type: ToastType.Danger,
        title: 'Fehler',
        body: 'Teilnehmer konnte nicht gefunden werden.',
      })
    )
  }
  // This triggers a soft page reload, so that the loaded data is in the form without some special behaviour
  navigate('/empty', {replace: true})
  navigate(routes.PARTICIPANT.path, {replace: true})
}

export const setParticipantViaToken = (
  token: string,
  navigate: NavigateFunction
): AppThunk => async (dispatch, getState) => {
  const { baseUrl, programId } = getState().misc
  const events = getState().events.list
  const requestData: TokenRequest = {
    program: programId,
    token: token,
  }
  try {
    const response = await axios({
      method: 'POST',
      url: `${baseUrl}/api/custom/verify/account/token`,
      data: qs.stringify(requestData),
    })
    const response_data = response.data[0]
    const registrations = response_data.registrations
    delete response_data.registrations
    response_data.date_of_birth = Moment(
      response_data.date_of_birth,
      'YYYY-MM-DD'
    ).format('DD.MM.YYYY')

    // set handicap from disabled
    if(response_data.disabled && response_data.disabled !== "") {
      response_data.handicap = response_data.disabled
      response_data.disabled = true
    } else {
      response_data.disabled = false
      response_data.handicap = ''
    }

    dispatch(setParticipant(response_data))
    registrations.forEach((reg: any) => {
      reg.event = events.find(
        (event: IEvent) => event.event_id === reg.event_id
      )
      dispatch(addTokenRegistration(reg))
      dispatch(addTokenOriginalRegistrationID(reg.event_id))
    })
    const paymentCodes: Record<string, string> = {
      '2_CASH': 'cash',
      '4_DEBIT': 'debit',
      '3_TRANSFER': 'transfer',
      '5_ONLINE': 'online',
      '6_INVOICE': 'invoice',
      '1_CUSTOM': 'custom',
    }
    dispatch(setPaymentMethod(paymentCodes[response_data.basic_fee_type]))
    dispatch(setLoadedViaCode(true))

    const answeredQuestions: any[] = []

    getState().program.data.participant_questions.forEach((answer: any) => {
      response_data.custom_answers.forEach((givenAnswer: CustomAnswer) => {
        if (givenAnswer.question_id === answer.id) {
          answeredQuestions.push(answer.id)
        }
      })
    })
    dispatch(setAnsweredParticipantCustomQuestions(answeredQuestions))
    dispatch(
      addToast({
        type: ToastType.Success,
        title: 'Daten erfolgreich geladen',
        body:
          'Bitte beachten Sie das bereits exisiterende' +
          ' Daten zwar angezeigt werden (Anmeldungen, persönliche Daten usw.) aber nicht angepasst werden können.',
      })
    )
  } catch (error: unknown) {
    console.error(error)
    dispatch(
      addToast({
        type: ToastType.Danger,
        title: 'Fehler',
        body: 'Teilnehmer konnte nicht gefunden werden.',
      })
    )
  }
  // This triggers a soft page reload, so that the loaded data is in the form without some special behaviour
  navigate('/empty', {replace: true})
  navigate(routes.EVENTS.path, {replace: true})
}

export const setCityViaZip = (
  zipCode: string,
  callback: Function
): AppThunk => async (dispatch, getState) => {
  const { baseUrl } = getState().misc
  if (zipCode && zipCode.length === 5) {
    try {
      const response = await axios({
        method: 'GET',
        url: `${baseUrl}/api/custom/validate/zipcode/?zip_code=${zipCode}`,
      })
      callback(response.data)
    } catch (error: unknown) {
      console.error(error)
    }
  }
}
